/* eslint-disable jsx-a11y/anchor-is-valid */
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import XLSX from 'xlsx';
import {
  Button,
  Icon,
  Modal,
  Checkbox,
  Divider,
  Popup,
  Message,
} from 'semantic-ui-react';
import { CSVLink } from 'react-csv';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import './ExportCSV.scss';
import Loader from 'components/common/PageLoader';
import capitalize from 'utils/capitalize';
import flattenObject from 'utils/flattenObject';
import FetchExportData from 'components/common/FetchExportData';
import clearExportData from 'redux/actions/export/clearExportData';
import { canPerformAction } from 'helpers/permissions';
import permissions from 'constants/permissions/';
import getUserRoles from 'helpers/getUserRoles';

const ExportCSV = ({
  data,
  fileName,
  customHeaders,
  customExport,
  fetchExportData,
  url,
}) => {
  const [dataHasBeenFetched, setDataHasBeenFetched] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedHeaders, setSelectedHeaders] = useState([]);
  const [selectedAll, setSelectedAll] = useState(true);
  const [CSVHeaders, setCSVHeaders] = useState([]);
  const [dataToExport, setDataToExport] = useState([]);

  const { pathname } = useLocation();

  const { data: fetchData, loading, error } = useSelector(
    ({ exportData }) => exportData,
  );

  const { roles, currentOrgId, orgUsers } = getUserRoles();

  const dispatch = useDispatch();

  const getLabel = key => capitalize(key.replace(/_/g, ' '));
  const renamedLabels = {
    user_first_name: getLabel('inviter_first_name'),
    user_last_name: getLabel('inviter_last_name'),
    user_middle_name: getLabel('inviter_middle_name'),
  };

  const getDotLabel = dotKey => {
    const parts = dotKey.split('.');
    return parts.map(p => getLabel(p)).join(' ');
  };

  const getChildDisplayLabel = dotKey => {
    const parts = dotKey.split('.');
    return parts
      .slice(1)
      .map(p => getLabel(p))
      .join(' ');
  };

  /**
   * Build grouped header structure from flattened data keys.
   * - Flat keys (no dot) => { label, key, isGroup: false, children: [] }
   * - Dot-notation keys => grouped under parent prefix
   *   { label: "User", key: "user", isGroup: true, children: [{ label, key }] }
   */
  const buildHeadersFromFlatData = flatRows => {
    const allKeys = new Set();
    flatRows.forEach(row => {
      Object.keys(row).forEach(k => allKeys.add(k));
    });

    const groupMap = new Map();
    const result = [];
    const insertionOrder = [];

    allKeys.forEach(key => {
      const dotIndex = key.indexOf('.');
      if (dotIndex === -1) {
        // Flat key
        const label = renamedLabels[key] || getLabel(key);
        if (!insertionOrder.includes(key)) {
          insertionOrder.push(key);
          result.push({ label, key, isGroup: false, children: [] });
        }
      } else {
        // Dot-notation key — group by prefix
        const prefix = key.substring(0, dotIndex);
        if (!groupMap.has(prefix)) {
          groupMap.set(prefix, []);
          insertionOrder.push(prefix);
        }
        const childLabel = getDotLabel(key);
        groupMap.get(prefix).push({ label: childLabel, key });
      }
    });

    // Build final ordered result
    const orderedResult = [];
    insertionOrder.forEach(entry => {
      if (groupMap.has(entry)) {
        orderedResult.push({
          label: renamedLabels[entry] || getLabel(entry),
          key: entry,
          isGroup: true,
          children: groupMap.get(entry),
        });
      } else {
        const found = result.find(r => r.key === entry);
        if (found) orderedResult.push(found);
      }
    });

    return orderedResult;
  };

  /**
   * Flatten all data rows using flattenObject utility.
   * Returns { flatData, headers } where headers is the grouped structure.
   */
  const buildFlatDataAndHeaders = sourceData => {
    const rows = Array.isArray(sourceData) ? sourceData : [];
    const flatRows = rows.map(item => flattenObject(item));

    const headers = buildHeadersFromFlatData(flatRows);
    return { flatRows, headers };
  };

  /**
   * Extract all leaf-level { key, label } entries from headers.
   * For groups, returns each child. For flat fields, returns the field itself.
   */
  const getLeafHeaders = hdrs =>
    hdrs.reduce((acc, header) => {
      if (header.isGroup && header.children.length > 0) {
        return [
          ...acc,
          ...header.children.map(c => ({
            key: c.key,
            label: c.label,
          })),
        ];
      }
      return [...acc, { key: header.key, label: header.label }];
    }, []);

  /**
   * Initialize flattened data and headers from source data.
   */
  const initializeExport = sourceData => {
    const { flatRows, headers } = buildFlatDataAndHeaders(sourceData);
    setDataToExport(flatRows);
    setCSVHeaders(headers);
    setSelectedHeaders(getLeafHeaders(headers));
    setSelectedAll(true);
  };

  /**
   * Toggle all children of a group on/off in selectedHeaders.
   */
  const handleGroupSelect = (header, checked) => {
    const childKeys = header.children.map(c => c.key);
    if (checked) {
      // Add all children that aren't already selected
      const existing = new Set(selectedHeaders.map(h => h.key));
      const newHeaders = [...selectedHeaders];
      header.children.forEach(child => {
        if (!existing.has(child.key)) {
          newHeaders.push({ key: child.key, label: child.label });
        }
      });
      setSelectedHeaders(newHeaders);
    } else {
      // Remove all children of this group
      setSelectedHeaders(
        selectedHeaders.filter(h => !childKeys.includes(h.key)),
      );
    }
  };

  /**
   * Toggle a single dot-notation column in selectedHeaders.
   */
  const handleChildSelect = (child, checked) => {
    if (checked) {
      setSelectedHeaders([
        ...selectedHeaders,
        { key: child.key, label: child.label },
      ]);
    } else {
      setSelectedHeaders(
        selectedHeaders.filter(h => h.key !== child.key),
      );
    }
  };

  /**
   * Handle flat (non-group) field toggle.
   */
  const handleSelect = ({ key, label, checked }) => {
    setSelectedHeaders(
      checked
        ? [...selectedHeaders, { key, label }]
        : selectedHeaders.filter(
            header => String(header.key) !== String(key),
          ),
    );
  };

  const [format, setFormat] = useState();

  const cleanUpAfterClose = () => {
    if (fetchExportData) {
      clearExportData()(dispatch);
      setDataHasBeenFetched(false);
    }
  };

  const handleExportToExcel = () => {
    const filterForExcelExport = [];

    dataToExport.forEach(item => {
      const filteredColumns = {};
      selectedHeaders.forEach(({ key, label }) => {
        filteredColumns[label] = item[key] != null ? item[key] : '';
      });
      filterForExcelExport.push(filteredColumns);
    });

    const binaryWS = XLSX.utils.json_to_sheet(filterForExcelExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, binaryWS, fileName);
    XLSX.writeFile(wb, `${fileName}.xlsx`);
    setOpenModal(false);
    cleanUpAfterClose();
  };

  const ExportButton = () =>
    customExport ? (
      <Button
        className="toolbar-export-csv-button-custom"
        icon
        size="mini"
        onClick={() => {
          initializeExport(data);
        }}
      >
        <Icon name="external alternate" />
      </Button>
    ) : (
      <Button
        className="toolbar-export-csv-button"
        onClick={() => {
          if (!fetchExportData) {
            initializeExport(data);
          }
        }}
      >
        <Icon name="external alternate" />
      </Button>
    );

  const selectOrDeselectedAll = () => {
    if (selectedAll) {
      setSelectedHeaders([]);
      setSelectedAll(false);
    } else {
      setSelectedHeaders(getLeafHeaders(CSVHeaders));
      setSelectedAll(true);
    }
  };

  const isHeaderSelected = key =>
    !!selectedHeaders.find(h => h.key === key);

  const areAllChildrenSelected = header =>
    header.children.length > 0 &&
    header.children.every(child => isHeaderSelected(child.key));

  const areSomeChildrenSelected = header =>
    header.children.some(child => isHeaderSelected(child.key));

  const exportCSVReady = () => {
    return (
      <>
        <Modal.Content scrolling>
          {(CSVHeaders || []).map(header => (
            <div className="field-to-export" key={header.key}>
              {header.isGroup ? (
                <>
                  <Checkbox
                    className="checkbox"
                    toggle
                    value={header.key}
                    label={header.label}
                    checked={areAllChildrenSelected(header)}
                    indeterminate={
                      areSomeChildrenSelected(header) &&
                      !areAllChildrenSelected(header)
                    }
                    onChange={(e, { checked }) =>
                      handleGroupSelect(header, checked)
                    }
                  />
                  {header.children.length > 0 && (
                    <div className="radio">
                      {header.children.map(child => (
                        <div
                          className="child-field-to-export"
                          key={child.key}
                        >
                          <Checkbox
                            className="checkbox"
                            toggle
                            label={getChildDisplayLabel(child.key)}
                            value={child.key}
                            checked={isHeaderSelected(child.key)}
                            onChange={(e, { checked }) =>
                              handleChildSelect(child, checked)
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Checkbox
                  className="checkbox"
                  toggle
                  value={header.key}
                  label={header.label}
                  checked={isHeaderSelected(header.key)}
                  onChange={(e, { checked }) =>
                    handleSelect({
                      key: header.key,
                      label: header.label,
                      checked,
                    })
                  }
                />
              )}
              <Divider />
            </div>
          ))}
        </Modal.Content>
        <Modal.Actions>
          {format === 'CSV' && (
            <CSVLink
              data={dataToExport}
              filename={fileName}
              headers={customHeaders || selectedHeaders}
              onClick={() => {
                setSelectedAll(true);
              }}
            >
              <Button
                primary
                onClick={() => {
                  setOpenModal(false);
                  cleanUpAfterClose();
                }}
              >
                Export
              </Button>
            </CSVLink>
          )}

          {format === 'Excel' && (
            <Button primary onClick={handleExportToExcel}>
              Export
            </Button>
          )}
        </Modal.Actions>
      </>
    );
  };

  const showSelectOrDeselectButton = () => {
    if (fetchExportData) {
      return dataHasBeenFetched ? 'show' : 'hide';
    }
    return 'show';
  };

  const fetchExportDataResult = () => {
    if (dataHasBeenFetched) {
      return exportCSVReady();
    }

    if (loading)
      return (
        <Modal.Content className="loader_container">
          <Loader />
        </Modal.Content>
      );

    if (error)
      return (
        <Modal.Content>
          <Message negative>Other error..{error?.message}</Message>
        </Modal.Content>
      );

    if (!loading && !!fetchData?.data) {
      data = fetchData.data;
      initializeExport(data);
      setDataHasBeenFetched(true);
    }
    return <FetchExportData url={url} />;
  };

  return (
    <div className="ExportCSV">
      {canPerformAction(
        permissions.canPerformAction.canExport,
        roles,
        {
          currentOrgId,
          orgUsers,
          additionalCondition: pathname === '/',
        },
      ) && (
        <>
          <Popup
            trigger={
              <div>
                <ExportButton />
              </div>
            }
            on="click"
            pinned
            position="bottom center"
            content={
              <div className="export-format">
                <Button
                  className="export-format-button"
                  onClick={() => {
                    setFormat('CSV');
                    setOpenModal(true);
                  }}
                >
                  CSV Format
                </Button>
                <Button
                  className="export-format-button"
                  onClick={() => {
                    setFormat('Excel');
                    setOpenModal(true);
                  }}
                >
                  Excel Format
                </Button>
              </div>
            }
          />
          <Modal
            size="mini"
            open={openModal}
            onClose={() => {
              setOpenModal(false);
              cleanUpAfterClose();
            }}
            closeIcon
          >
            <Modal.Header>
              Export to {format}
              <div
                className={`whole_selector ${showSelectOrDeselectButton()}`}
                onClick={selectOrDeselectedAll}
              >
                {selectedAll ? 'DESELECT ALL' : 'SELECT ALL'}
              </div>
            </Modal.Header>
            {fetchExportData
              ? fetchExportDataResult()
              : exportCSVReady()}
          </Modal>
        </>
      )}
    </div>
  );
};

ExportCSV.defaultProps = {
  data: [],
  fileName: 'file.csv',
  customHeaders: null,
  customExport: false,
  fetchExportData: false,
  url: '',
};

ExportCSV.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array]),
  fileName: PropTypes.string,
  customExport: PropTypes.bool,
  customHeaders: PropTypes.oneOfType([PropTypes.array]),
  fetchExportData: PropTypes.bool,
  url: PropTypes.string,
};

export default ExportCSV;
